import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaEye, FaArrowRight, FaStore, FaPrint } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchPharmacyBranches, clearError } from '../../../redux/Slices/AdminPharmaciesSlice';
import PrintInvoicesModal from '../../../shared/components/PrintInvoicesModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminPharmacyBranches = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { pharmacyId } = useParams();
    
    const { branches, branchesPagination, branchesLoading, error } = useSelector((state) => state.adminPharmacies);
    
    const [currentPage, setCurrentPage] = useState(1);
    const [printModal, setPrintModal] = useState({ isOpen: false, storeId: null });

    useEffect(() => {
        setTitle('فروع الصيدلية');
    }, [setTitle]);

    useEffect(() => {
        dispatch(fetchPharmacyBranches({ pharmacyId, page: currentPage }));
    }, [dispatch, pharmacyId, currentPage]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleViewDetails = (branchId) => {
        navigate(`/admin-pharmacies/${pharmacyId}/branches/${branchId}`);
    };

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success text-white">نشط</span>;
        }
        return <span className="badge bg-danger text-white">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger text-white">محظور</span>;
        }
        return <span className="badge bg-success text-white">غير محظور</span>;
    };

    const renderPagination = () => {
        const totalPages = branchesPagination?.last_page || 1;
        const pages = [];
        
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => setCurrentPage(i)}>
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Page navigation">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}>
                            السابق
                        </button>
                    </li>
                    {pages}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}>
                            التالي
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">فروع الصيدلية</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn text-white"
                        onClick={() => navigate(`/admin-pharmacies/${pharmacyId}`)}
                        style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                        <FaArrowRight className="mx-2" />
                        العودة للتفاصيل
                    </motion.button>
                </div>

                {/* Branches Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {branchesLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الفروع...</p>
                            </div>
                        ) : !Array.isArray(branches) || branches.length === 0 ? (
                            <div className="text-center py-5">
                                <FaStore className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد فروع</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>اللوجو</th>
                                                <th>اسم الفرع</th>
                                                <th>رقم الهاتف</th>
                                                <th>البريد الإلكتروني</th>
                                                <th>حالة النشاط</th>
                                                <th>حالة الحظر</th>
                                                <th>تاريخ التسجيل</th>
                                                <th>الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {branches.map((branch, index) => (
                                                <tr key={branch.id}>
                                                    <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                    <td>
                                                        <div className="d-flex justify-content-center">
                                                            <div style={{ width: '50px', height: '50px' }}>
                                                                {branch.logo && branch.logo !== 'logo' && (
                                                                    <img
                                                                        src={branch?.logo}
                                                                        alt={branch.market_name}
                                                                        className="rounded w-100 h-100"
                                                                        style={{ objectFit: 'cover' }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="fw-medium">{branch.market_name || 'غير محدد'}</div>
                                                    </td>
                                                    <td>{branch.phone || 'غير محدد'}</td>
                                                    <td>{branch.email || 'غير محدد'}</td>
                                                    <td>
                                                        {getStatusBadge(branch.status)}
                                                    </td>
                                                    <td>
                                                        {getBanBadge(branch.ban)}
                                                    </td>
                                                    <td>
                                                        {branch.created_at ? new Date(branch.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <motion.button
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-sm text-white"
                                                                onClick={() => handleViewDetails(branch.id)}
                                                                title="عرض التفاصيل"
                                                                style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                                                <FaEye />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-sm text-white"
                                                                onClick={() => setPrintModal({ isOpen: true, storeId: pharmacyId })}
                                                                title="طباعة فواتير"
                                                                style={{ backgroundColor: '#10b981' }}>
                                                                <FaPrint style={{ fontSize: '1rem' }} />
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <PrintInvoicesModal
                isOpen={printModal.isOpen}
                onClose={() => setPrintModal({ isOpen: false, storeId: null })}
                storeId={printModal.storeId}
                baseUrl={BASE_URL}
            />
        </div>
    );
};

export default AdminPharmacyBranches;

// CSS للتصميم
const style = document.createElement('style');
style.textContent = `
    .table-hover tbody tr:hover {
        background-color: rgba(24, 65, 148, 0.05) !important;
    }
    
    /* تصميم اللوجو */
    .table td img {
        max-width: 100% !important;
        max-height: 100% !important;
        object-fit: cover !important;
        border-radius: 8px !important;
    }
    
    .table td {
        vertical-align: middle !important;
        padding: 1rem 0.75rem !important;
    }
    
    /* محاذاة الأعمدة */
    .table th:nth-child(1),
    .table td:nth-child(1) {
        text-align: center !important;
        width: 60px !important;
    }
    
    .table th:nth-child(2),
    .table td:nth-child(2) {
        text-align: center !important;
        width: 80px !important;
    }
    
    .table th:nth-child(3),
    .table td:nth-child(3) {
        text-align: center !important;
        min-width: 200px !important;
    }
    
    .table th:nth-child(4),
    .table td:nth-child(4) {
        text-align: center !important;
    }
    
    .table th:nth-child(5),
    .table td:nth-child(5) {
        text-align: center !important;
    }
    
    .table th:nth-child(6),
    .table td:nth-child(6) {
        text-align: center !important;
    }
    
    .table th:nth-child(7),
    .table td:nth-child(7) {
        text-align: center !important;
    }
    
    .table th:nth-child(8),
    .table td:nth-child(8) {
        text-align: center !important;
    }
    
    .table th:nth-child(9),
    .table td:nth-child(9) {
        text-align: center !important;
    }
    
    /* تصميم اسم الفرع */
    .table td:nth-child(3) {
        font-weight: 500 !important;
        color: #374151 !important;
    }
`;

if (typeof document !== 'undefined') {
    document.head.appendChild(style);
}
